import { useSelector } from "react-redux";

import { useGetRolesQuery } from "state/rtkQuery/access/accessQueries";
import { permissionSetGrants } from "utils/accessManagement/roleDisplay";
import { isCrossOrgAdminRole } from "./RequireEcrOpsRole";
import { useCurrentUserRoleNames } from "./useAccessPermissions";
import { useEffectiveOrgRole } from "./useEffectiveOrgRole";

// usePermission()/useAccessPermissions() union the permissions[] of EVERY
// role a user holds anywhere (GET /user/roles' flat, non-org-scoped role
// list) into one global set. That's correct for the 3 real platform-wide
// roles (EnterpriseAdmin/OperationsAdmin/OperationsUser — see
// isCrossOrgAdminRole), which genuinely act across every org. But for a
// per-org role (OrganizationOwner/OrganizationApprover/Leadership), that
// global union is wrong: being Owner of org A must not grant Owner-level
// permissions on org B just because both roles happen to live in the same
// flat union. Concretely — OrganizationOwner's role definition includes
// user:id:update; Leadership's does not — so a user who is Owner of org A
// and Leadership of org B must NOT see org B's user:id:update-gated controls,
// even though their global permission union (correctly) contains
// user:id:update from org A's Owner assignment.
//
// This hook answers the question those pages actually need: "does the role I
// hold on THIS SPECIFIC org grant this permission?" — by looking up the
// role name GET /user/organizations reports for this org (e.g. "Leadership")
// against that SAME role's own permissions[] in GET /roles (already
// fetched/cached by useAccessPermissions, so this is normally a cache hit).
// This is fully dynamic: it works for any role name GET /user/organizations
// ever returns for an org, not just the 3 known today — a brand-new
// org-scoped role added on the backend tomorrow needs no frontend change.
//
// The one deliberate exception: a real cross-org-admin role (verified via
// isCrossOrgAdminRole, not just "some permission happens to be true") still
// falls back to the global union, since those 3 roles are the only ones with
// no org_id at all — there is no per-org role to look up for them.
export const useEffectiveOrgPermission = (orgId, permissionKey) => {
  const { role: orgRoleName, isLoading: isOrgRoleLoading } = useEffectiveOrgRole(orgId);
  const roleNames = useCurrentUserRoleNames();
  const globalPermissions = useSelector((state) => state.accessPermissions.permissions);
  const isCrossOrgAdmin = isCrossOrgAdminRole(roleNames);

  const { data: rolesData, isLoading: isRolesLoading } = useGetRolesQuery(
    { perPage: 200 },
    { skip: isCrossOrgAdmin }
  );

  if (isCrossOrgAdmin) {
    return permissionSetGrants(globalPermissions, permissionKey);
  }

  if (isOrgRoleLoading || isRolesLoading || !orgRoleName) return false;

  const role = rolesData?.roles?.find((r) => r.role_name === orgRoleName);
  return permissionSetGrants(role?.permissions, permissionKey);
};

// Convenience for checking several permissions on the current org's role at
// once (e.g. the Copilot usage-read bundle) without repeating the lookup.
export const useEffectiveOrgPermissions = (orgId, permissionKeys) => {
  const { role: orgRoleName, isLoading: isOrgRoleLoading } = useEffectiveOrgRole(orgId);
  const roleNames = useCurrentUserRoleNames();
  const globalPermissions = useSelector((state) => state.accessPermissions.permissions);
  const isCrossOrgAdmin = isCrossOrgAdminRole(roleNames);

  const { data: rolesData, isLoading: isRolesLoading } = useGetRolesQuery(
    { perPage: 200 },
    { skip: isCrossOrgAdmin }
  );

  if (isCrossOrgAdmin) {
    return permissionKeys.map((key) => permissionSetGrants(globalPermissions, key));
  }

  if (isOrgRoleLoading || isRolesLoading || !orgRoleName) {
    return permissionKeys.map(() => false);
  }

  const role = rolesData?.roles?.find((r) => r.role_name === orgRoleName);
  return permissionKeys.map((key) => permissionSetGrants(role?.permissions, key));
};
